import React, { useState, useRef } from 'react';
import {
  IconButton, Popover, Box, Typography, TextField, InputAdornment,
  Button, Stack, Divider, Snackbar, Tooltip,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

/**
 * "Share" arrow that opens a small popup showing the EXACT link with a one-tap copy button,
 * plus WhatsApp / Email share options. We deliberately don't use the native OS share sheet:
 * its "Copy" action bundles the title + message + url together, whereas users expect
 * "Copy link" to copy just the URL. This popup makes the copied value explicit and predictable.
 *
 * Props:
 *   url     – the link to share (required)
 *   title   – email subject (default "sqip")
 *   text    – accompanying message for WhatsApp / Email (default "Join the queue")
 *   size, color, sx, tooltip – passed to the trigger IconButton / Tooltip
 */
export default function ShareButton({
  url,
  title = 'sqip',
  text = 'Join the queue',
  size = 'small',
  color = 'inherit',
  sx,
  tooltip = 'Share',
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snack, setSnack] = useState('');
  const inputRef = useRef(null);

  const message = `${text} ${url}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;

  const open = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!url) return;
    setCopied(false);
    setAnchorEl(e.currentTarget);
  };
  const close = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  const copyLink = async (e) => {
    e.stopPropagation();
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // Clipboard API blocked (e.g. non-secure context) — fall back to selecting + execCommand.
      try {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.select();
          ok = document.execCommand('copy');
        }
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      setSnack('Link copied!');
      setTimeout(() => setCopied(false), 1600);
    } else {
      setSnack('Could not copy — select the link and copy manually.');
    }
  };

  const openExternal = (href) => (e) => {
    e.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton size={size} color={color} onClick={open} sx={sx} aria-label="share">
          <IosShareIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, maxWidth: '90vw', borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
          Share link
        </Typography>
        <TextField
          value={url}
          size="small"
          fullWidth
          inputRef={inputRef}
          InputProps={{
            readOnly: true,
            sx: { fontSize: 13 },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                  <IconButton edge="end" size="small" onClick={copyLink} color={copied ? 'success' : 'primary'}>
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          onFocus={(e) => e.target.select()}
        />
        <Button
          fullWidth
          variant="contained"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copyLink}
          sx={{ mt: 1.5, borderRadius: 2 }}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>

        <Divider sx={{ my: 1.5 }}>
          <Typography variant="caption" color="text.secondary">or share via</Typography>
        </Divider>

        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
            onClick={openExternal(whatsappUrl)}
            sx={{ borderRadius: 2 }}
          >
            WhatsApp
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<EmailOutlinedIcon />}
            onClick={openExternal(emailUrl)}
            sx={{ borderRadius: 2 }}
          >
            Email
          </Button>
        </Stack>
      </Popover>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={1800}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
